/**
 * TaskStateControls — what a task card says about its own state, and the one
 * control that changes it (#904): the due date in the viewer's zone (red when
 * overdue, amber when due today), a status pill, and Complete / Reopen.
 *
 * Both buttons go through lib/tasksApi, which tells the Tasks page and the
 * sidebar badge to refetch; `onSaved` receives the updated row so the host can
 * merge it in place. They render from the server's `can_edit` and the server
 * re-checks it, so a disabled button is a courtesy and never the control.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../lib/apiErrors';
import { completeTask, notifyTasksChanged, reopenTask } from '../../lib/tasksApi';
import {
    dueDayKey, dueState, dueText, dueTone, isTaskClosed, taskStatusLabel,
} from '../../lib/taskDue';

const CHIP_TONES = {
    overdue: 'bg-red-50 border-red-200',
    today: 'bg-amber-50 border-amber-200',
    upcoming: 'bg-white border-stone-200',
    none: 'bg-white border-stone-200',
    closed: 'bg-stone-50 border-stone-200',
};

const PILL_TONES = {
    Open: 'bg-blue-50 text-blue-700 border-blue-200',
    Done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const PILL_TONE_OTHER = 'bg-stone-100 text-stone-500 border-stone-200';

// The compare-and-swap refusal: someone moved the task's status while this card was open.
const isStatusChangedConflict = (err) => {
    const detail = err?.response?.data?.detail;
    return err?.response?.status === 409 && (detail?.code ?? detail?.error) === 'task_status_changed';
};

export default function TaskStateControls({ activity, zone, onSaved, compact = false }) {
    const auth = useAuth() || {};
    const getAuthHeader = auth.getAuthHeader || (() => ({}));
    const [busy, setBusy] = useState(false);
    if (!activity || activity.type !== 'task') return null;

    const activityId = activity.activity_id || activity.id;
    const closed = isTaskClosed(activity);
    const hasDate = Boolean(dueDayKey(activity.due_date));
    // A finished task keeps its date but never shows as overdue.
    const state = closed ? 'closed' : dueState(activity.due_date, zone);
    const canEdit = Boolean(activity.can_edit) && Boolean(activityId);
    const statusLabel = taskStatusLabel(activity);

    const change = async (write, success, failure) => {
        if (busy) return;
        setBusy(true);
        try {
            const next = await write(activityId, getAuthHeader());
            toast.success(success);
            onSaved?.(next);
        } catch (err) {
            toast.error(errorText(err, failure));
            // The write itself never notified (it failed), but the task did change under this card: refresh the lists and badge.
            if (isStatusChangedConflict(err)) notifyTasksChanged();
        } finally {
            setBusy(false);
        }
    };

    const size = compact ? 'text-[10px] px-1.5 py-0.5' : 'text-[11px] px-2 py-0.5';
    return (
        <div className={`flex items-center gap-2 flex-wrap ${compact ? '' : 'mt-1'}`} data-testid="task-state-controls">
            {(hasDate || !closed) && (
                <span
                    data-testid="task-due"
                    data-due-state={state}
                    className={`inline-flex items-center rounded border ${size} ${CHIP_TONES[state]} ${dueTone(state)}`}
                >
                    {hasDate ? dueText(activity.due_date, state, zone) : 'No due date'}
                </span>
            )}
            <span
                data-testid="task-status-pill"
                className={`inline-flex items-center rounded-full border ${size} font-medium ${PILL_TONES[statusLabel] || PILL_TONE_OTHER}`}
            >
                {statusLabel}
            </span>
            {closed ? (
                <button
                    type="button"
                    data-testid="task-reopen"
                    disabled={!canEdit || busy}
                    title={canEdit ? undefined : 'You can’t change this task'}
                    onClick={() => change(reopenTask, 'Task reopened', 'Failed to reopen task')}
                    className={`rounded border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed ${size}`}
                >
                    Reopen
                </button>
            ) : (
                <button
                    type="button"
                    data-testid="task-complete"
                    disabled={!canEdit || busy}
                    title={canEdit ? undefined : 'You can’t change this task'}
                    onClick={() => change(completeTask, 'Task completed', 'Failed to complete task')}
                    className={`rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed ${size}`}
                >
                    Complete
                </button>
            )}
        </div>
    );
}
